import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Sparkles, UserRound, Wallet, LogOut } from "lucide-react";
import { useCustomer } from "@/context/CustomerContext";
import { NotificationBell } from "@/components/account/NotificationBell";
import { egp } from "@/lib/labels";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "./Logo";
import { NAV } from "@/lib/site";

const linkCls = ({ isActive }) =>
  `font-head relative px-3 py-2 text-[15px] font-semibold transition-colors duration-200 ${
    isActive ? "text-white after:absolute after:inset-x-3 after:-bottom-0.5 after:h-[3px] after:rounded-full after:bg-[var(--km-pink)]" : "text-[var(--km-lilac)] hover:text-white"
  }`;

export const CtaButton = ({ className = "", testId = "header-create-ad-btn", onClick }) => (
  <Link
    to="/create-ad"
    onClick={onClick}
    data-testid={testId}
    className={`font-head group inline-flex items-center gap-2 rounded-full bg-[var(--km-pink)] px-6 py-3 font-bold text-white shadow-[0_6px_0_#4B1782] transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-[0_8px_0_#4B1782] active:translate-y-1 active:shadow-[0_2px_0_#4B1782] ${className}`}
  >
    <Sparkles className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
    اعمل إعلانك
  </Link>
);

const AccountLink = ({ mobile, onClick }) => {
  const { customer } = useCustomer();
  const label = customer ? "حسابي" : "دخول";
  return (
    <Link to={customer ? "/account" : "/login"} onClick={onClick} data-testid={mobile ? "mobile-account-link" : "header-account-link"}
      className={mobile ? "font-display rounded-2xl px-4 py-3 text-3xl text-[var(--km-lilac)]" : "font-head hidden items-center gap-1.5 rounded-full border border-purple-500/40 px-4 py-2 text-sm font-bold text-white transition-colors hover:border-white sm:inline-flex"}>
      {!mobile && <UserRound className="h-4 w-4" />} {label}
    </Link>
  );
};

const LogoutButton = ({ mobile, onClick }) => {
  const { customer, logout } = useCustomer();
  const nav = useNavigate();
  if (!customer) return null;
  const out = async () => {
    onClick?.();
    await logout();
    nav("/");
  };
  return mobile ? (
    <button onClick={out} className="font-display rounded-2xl px-4 py-3 text-right text-3xl text-red-300" data-testid="mobile-logout-btn">خروج</button>
  ) : (
    <button onClick={out} aria-label="خروج" data-testid="header-logout-btn"
      className="hidden h-10 w-10 place-items-center rounded-2xl border border-purple-500/30 bg-[var(--km-card)] text-white hover:bg-red-500 sm:grid">
      <LogOut className="h-5 w-5" />
    </button>
  );
};

const CustomerControls = () => {
  const { customer } = useCustomer();
  if (!customer) return null;
  if (customer.must_change_password) return <LogoutButton />;
  return (
    <>
      <Link to="/wallet" data-testid="header-wallet-link" aria-label="محفظتي"
        className="font-head inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-full bg-[var(--km-purple)] px-3 text-sm font-bold text-white transition-colors hover:bg-[var(--km-pink)]">
        <Wallet className="h-4 w-4" /> <span data-testid="header-wallet-balance">{egp(customer.wallet_balance)}</span>
      </Link>
      <NotificationBell />
      <LogoutButton />
    </>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="km-glass sticky top-0 z-50 border-b border-purple-500/20" data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-1 lg:flex" aria-label="القائمة الرئيسية">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end className={linkCls} data-testid={`nav-${n.id}-link`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <CustomerControls />
          <AccountLink />
          <CtaButton className="hidden !px-5 !py-2.5 text-sm sm:inline-flex" />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="grid h-11 w-11 place-items-center rounded-xl border border-purple-500/30 text-white lg:hidden" aria-label="افتح القائمة" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] border-purple-500/30 bg-[var(--km-bg2)] text-white" dir="rtl">
              <SheetTitle className="sr-only">القائمة</SheetTitle>
              <div className="mt-8 flex flex-col gap-2">
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} end onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.id}-link`}
                    className={({ isActive }) => `font-display rounded-2xl px-4 py-3 text-3xl ${isActive ? "bg-[var(--km-purple)] text-white" : "text-[var(--km-lilac)]"}`}>
                    {n.label}
                  </NavLink>
                ))}
                <AccountLink mobile onClick={() => setOpen(false)} />
                <LogoutButton mobile onClick={() => setOpen(false)} />
                <CtaButton className="mt-6 justify-center" testId="mobile-create-ad-btn" onClick={() => setOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
